import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import { my_routes } from "../router/all_routes";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

// importations liées aux années universitaire
import { useAcademicYear } from "../../core/common/context/AcademicYearContext";

// importation liées aux inscription/etudiant
import { getStudentById, updateStudent } from "../../api/etudiants";

import { toast } from "sonner";
import InfosAlertYearDisabled from "../../core/common/alerts/infosAlertYearDisabled";
import { deleteAvatar, getAvatarFileById } from "../../api/avatar";
import UploadPhotoModal from "../avatar/UploadPhotoModal";
import { Popconfirm } from "antd";

const sexe = [
  { value: "Masculin", label: "Masculin" },
  { value: "Feminin", label: "Feminin" },
];

// Shema du formulaire pour modifier l'insription de l'étudiant
const editStudentShema = z.object({
  lastname: z.string().nonempty("Veuillez insérer un nom"),
  firstname: z.string().nonempty("Veuillez insérer un prénom"),
  sexe: z
    .object(
      {
        value: z.string(),
        label: z.string(),
      },
      {
        required_error: "Veuillez choisir un genre valide",
        invalid_type_error: "Veuillez choisir un genre valide",
      },
    )
    .refine(
      (val) => val && val.value && val.label,
      "veuillez choisir un genre valide",
    ),
  birthday: z
    .any()
    .refine(
      (val) => val && dayjs(val).isValid(),
      "Veuillez entrer une date de naissance valide",
    ),
  email: z
    .string()
    // .email("Veuillez fournir une adresse email valide")
    .optional(),
  number: z
    .string()
    // .regex(
    //   /^(\+223)?[456789]\d{7}$/,
    //   "Numéro de téléphone invalide. Ex: +22370123456 ou 70123456"
    // )
    .optional(),
  adresse: z.string().optional(),
  place_birth: z.string().optional(),
  // .nonempty("Veuillez insérer un le lieu de naissance"),
  statut: z.boolean(),
});
// type RegisterFormFields = z.infer<typeof studentShema>;

type EditStudentFomrFields = z.infer<typeof editStudentShema>;

const EditStudentRegistration = () => {
  const { id } = useParams(); // On récupère l'id de l'URL

  const routes = my_routes;
  const navigate = useNavigate();

  const { selectedYear } = useAcademicYear();
  const [studentEdited, setStudentEdited] = useState<any>();
  const [avatar, setAvatar] = useState<any>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  //   Iniatialisation de zod avec react-hook-form pour l'edition
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<EditStudentFomrFields>({
    resolver: zodResolver(editStudentShema),
  });

  // fonction pour recuperer les infos de l'etudiant
  const getStudentInfoForEdit = async (id_student: string) => {
    try {
      const response = await getStudentById(id_student);

      const avatar = await getAvatarFileById("StudentProfil", id_student);
      setAvatar(avatar.data);

      // console.log("response : ",response.data)
      setStudentEdited(response.data);
      setValue("birthday", response.data.birthday);
      setValue("firstname", response.data.firstname);
      setValue("lastname", response.data.lastname);
      setValue("place_birth", response.data.place_birth);
      setValue("sexe", {
        value: response.data.sexe,
        label: response.data.sexe,
      });
      setValue("statut", response.data.statut);
      setValue("number", response.data.number);
      setValue("adresse", response.data.adresse);
      setValue("email", response.data.email ?? "");
    } catch (error: any) {
      // console.log("erreur récupération infos student: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  useEffect(() => {
    if (!id) {
      toast.error("Aucun id trouvé dans l'URL.");
      return;
    }
    getStudentInfoForEdit(id);
  }, [id]);

  // Fonction de soumission du formulaire
  const onSubmit: SubmitHandler<EditStudentFomrFields> = async (data) => {
    try {
      // on convertie le champ statut et isDeceased en boolean
      const formData = {
        ...data,
        email: data.email === "" ? undefined : data.email,
        sexe: data.sexe.value,
      };
      if (id) await updateStudent(id, formData);
      else throw new Error("id introuvable");

      toast.success("Etudiant modifié avec succès");
      navigate(routes.allStudent);
    } catch (error: any) {
      // Gérer les erreurs de l'API
      // console.log("erreur modificationn student:", error);
      setError("root", {
        message:
          error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "une erreur inattendu c'est produite veuillez ésssyez plus tard",
      });
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "une erreur inattendu c'est produite veuillez ésssyez plus tard",
      );
    }
  };

  const handleAddPhoto = () => {
    setShowUploadModal(true);
  };

  const handleDeletePhoto = async () => {
    try {
      if (!avatar || !id) return;
      await deleteAvatar(avatar?.id);
      toast.success("Photo supprimee avec succes");
      getStudentInfoForEdit(id);
    } catch (error: any) {
      // console.log("Erreur lors de la suppression: ", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  return (
    <>
      {/* Page wrapper */}
      <div className="page-wrapper">
        <div className="content content-two">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="mb-1">Modifier Etudiant</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.allStudent}>Etudiants</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Modifier Etudiant
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}
          {!selectedYear?.statut && <InfosAlertYearDisabled />}
          <div className="row">
            <div className="col-md-12 ">
              <form onSubmit={handleSubmit(onSubmit)}>
                {/* Information personnel */}
                <div className="card">
                  <div className="card-header bg-light">
                    <div className="d-flex align-items-center">
                      <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                        <i className="ti ti-info-square-rounded fs-16" />
                      </span>
                      <h4 className="text-dark">Information Personnel</h4>
                    </div>
                  </div>
                  <div className="card-body pb-1">
                    <div className="row">
                      <div className="col-md-12">
                        <div className="d-flex align-items-center flex-wrap row-gap-3 mb-3">
                          <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-2 flex-shrink-0 text-dark frames">
                            {avatar ? (
                              <img
                                src={avatar?.temporaryUrl}
                                alt={`${studentEdited?.lastname} ${studentEdited?.firstname}`}
                              />
                            ) : (
                              <i className="ti ti-photo-plus fs-16" />
                            )}
                          </div>
                          <div className="profile-upload">
                            <div className="profile-uploader d-flex align-items-center">
                              <div
                                className="drag-upload-btn mb-3"
                                onClick={() => handleAddPhoto()}
                              >
                                Upload
                              </div>
                              <Popconfirm
                                title="Êtes-vous sûr de vouloir supprimer cette photo ?"
                                onConfirm={() => handleDeletePhoto()}
                                disabled={!avatar?.temporaryUrl}
                                okText="Oui"
                                cancelText="Non"
                              >
                                <Link to="#" className="btn btn-primary mb-3">
                                  Remove
                                </Link>
                                {/*<Button icon={<DeleteOutlined />} danger />*/}
                              </Popconfirm>
                            </div>
                            <p className="fs-12">
                              Upload image size 4MB, Format JPG, PNG, SVG
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row row-cols-xxl-5 row-cols-md-6">
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Prénom</label>
                          <input
                            type="text"
                            placeholder="Entrer un prénom"
                            className={
                              errors.firstname
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            defaultValue={studentEdited?.firstname}
                            {...register("firstname")}
                          />

                          {errors.firstname && (
                            <div className="invalid-feedback">
                              {errors?.firstname.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Nom</label>
                          <input
                            type="text"
                            placeholder="Entrer un nom"
                            className={
                              errors.lastname
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            defaultValue={studentEdited?.lastname}
                            {...register("lastname")}
                          />
                          {errors.lastname && (
                            <div className="invalid-feedback">
                              {errors.lastname.message}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Genre</label>
                          <select
                            className={
                              errors.sexe
                                ? "form-select is-invalid"
                                : "form-select"
                            }
                            value={watch("sexe")?.value || ""}
                            onChange={(e) => {
                              const selected = sexe.find(
                                (s) => s.value === e.target.value,
                              );
                              setValue(
                                "sexe",
                                selected || { value: "", label: "" },
                              );
                              clearErrors("sexe");
                            }}
                          >
                            <option value="">Sélectionner le genre</option>
                            {sexe.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          {errors.sexe && (
                            <div className="invalid-feedback">
                              {errors.sexe.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Date d'anniversaire
                          </label>
                          <div className="input-icon position-relative">
                            {
                              <input
                                type="date"
                                className={
                                  errors.birthday
                                    ? "form-control datetimepicker is-invalid"
                                    : "form-control datetimepicker"
                                }
                                // value={studentEdited?.birthday}
                                placeholder="DD-MM-YYYY"
                                {...register("birthday")}
                              />
                            }

                            {errors.birthday && (
                              <div className="invalid-feedback">
                                {errors.birthday &&
                                typeof errors.birthday.message === "string"
                                  ? errors.birthday.message
                                  : null}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Numéro de Téléphone:
                          </label>
                          <input
                            type="text"
                            placeholder="Ex. +223 84230231"
                            className={
                              errors.number
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            defaultValue={studentEdited?.number}
                            {...register("number")}
                          />
                          {errors.number && (
                            <div className="invalid-feedback">
                              {errors.number.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">Adresse Mail</label>
                          <input
                            type="email"
                            placeholder="Ex. example@gmail.com"
                            className={
                              errors.email
                                ? "form-control  is-invalid"
                                : "form-control "
                            }
                            defaultValue={studentEdited?.email ?? ""}
                            {...register("email")}
                          />
                          {errors.email && (
                            <div className="invalid-feedback">
                              {errors.email.message}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-xxl col-xl-3 col-md-6  d-flex justify-content-center align-items-center">
                        <div className="form-check form-check-lg form-switch mb-3">
                          <label
                            className="form-check-label"
                            htmlFor="switch1-lg"
                          >
                            Statut
                          </label>
                          <input
                            {...register("statut")}
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id="switch1-lg"
                          />
                        </div>
                        {errors.statut && (
                          <div className="invalid-feedback">
                            {errors.statut.message}
                          </div>
                        )}
                      </div>

                      <div className="col-xxl col-xl-3 col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Lieu de naissance
                          </label>
                          <input
                            type="text"
                            placeholder="Entrer un lieu"
                            className={
                              errors.place_birth
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                            // defaultValue={studentEdited?.place_birth}
                            {...register("place_birth")}
                          />

                          {errors.place_birth && (
                            <div className="invalid-feedback">
                              {errors?.place_birth.message}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-xxl-12 col-md-12 col-xl-12">
                        <div className="mb-3">
                          <label className="form-label">Adresse Physique</label>
                          <textarea
                            {...register("adresse")}
                            placeholder="Exemple : Badalabougou, Commune 5, Rue 300 Porte 218"
                            rows={4}
                            cols={8}
                            className={
                              errors.adresse
                                ? "form-control is-invalid"
                                : "form-control"
                            }
                          />
                          {errors.adresse && (
                            <div className="invalid-feedback">
                              {errors.adresse.message}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Information personnel */}

                {/* /Adresse */}
                <div className="text-end">
                  <Link
                    to={routes.allStudent}
                    type="button"
                    className="btn btn-light me-3"
                  >
                    Annuler
                  </Link>
                  {isSubmitting ? (
                    <button
                      className="btn btn-primary-light"
                      type="button"
                      disabled={true}
                    >
                      <span
                        className="spinner-border spinner-border-sm align-middle me-1 "
                        role="status"
                        aria-hidden="true"
                      />
                      Loading...
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="btn btn-primary"
                      // data-bs-dismiss="modal"
                    >
                      Enregistrer Etudiant
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* /Page wrapper */}
      <UploadPhotoModal
        profilId={id}
        profilType="StudentProfil"
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={() => {
          if (id) getStudentInfoForEdit(id);
        }}
      />
    </>
  );
};

export default EditStudentRegistration;
